import { Alert, Box, Button, FormControl, IconButton, InputAdornment, InputLabel, MenuItem, Select, TextField } from "@mui/material"
import Grid from "@mui/material/Grid2"
import * as React from "react"
import { moneyFormat, parseMoneyInput } from "../../Utils"
import AttachMoneyIcon from '@mui/icons-material/AttachMoney'
import AddCircleSharpIcon from '@mui/icons-material/AddCircleSharp';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmModal from '../modals/ConfirmModal';
import { useTrackedDarkMode } from "../UserState"
import dayjs from "dayjs"
import { findServiceOrderFee, useServiceOrderFees } from "../ReactQuery/QueryServiceOrderFees"

function WarrantyLine({ amount, total_covered, total }) {
    return (
        <Grid container alignItems={'center'} size={12}>
            {/* WARRANTY ROW */}
            <Grid sx={{ pl: 4 }} size={5}>
                <span>Warranty</span>
            </Grid>
            <Grid size={3}>
                {total_covered !== total &&
                    <Alert severity="warning">Partial Warranty Coverage</Alert>
                }
            </Grid>
            <Grid sx={{ textAlign: 'right', pr: 4 }} size={4}>
                <span>-${moneyFormat(amount)}</span>
            </Grid>
        </Grid>
    )
}

export default function ServiceOrderQuoteInvoiceExtras({
    serviceOrder,
    calculatedCosts,
    setCalculatedCosts,
    isQuote,
    warrantiedCharges
}) {
    const [darkMode,] = useTrackedDarkMode();
    const borderColor = darkMode ? '#FFF' : 'rgba(0, 0, 0, 0.87)'
    const { data: serviceOrderFees } = useServiceOrderFees();
    const [confirmModal, setConfirmModal] = React.useState({
        open: false,
        text: '',
        callback: null
    })

    const updateFeeType = (id) => (e) => {
        const new_fee = findServiceOrderFee(serviceOrderFees, e.target.value)
        calculatedCosts.fees[id] = {
            ...calculatedCosts.fees[id],
            name: new_fee.name,
            description: new_fee.description,
            calculation_data: { ...new_fee.calculation_data, fee_type: new_fee.fee_type },
            edited: false,
            sku: new_fee.sku,
            service_order_fee_id: new_fee.id,
        }
        setCalculatedCosts({ ...calculatedCosts })
    }

    const updateFeeValue = (id) => (e) => {
        if (isQuote) {
            calculatedCosts.fees[id].estimated_cost = parseMoneyInput(calculatedCosts.fees[id].actual_cost, e.target.value, { allow_empty: true, allow_negative: true })
            calculatedCosts.fees[id].actual_cost = parseMoneyInput(calculatedCosts.fees[id].actual_cost, e.target.value, { allow_empty: true, allow_negative: true })
        } else {
            calculatedCosts.fees[id].actual_cost = parseMoneyInput(calculatedCosts.fees[id].actual_cost, e.target.value, { allow_empty: true, allow_negative: true })
        }
        calculatedCosts.fees[id].edited = true
        setCalculatedCosts({ ...calculatedCosts })
    }

    // const updateFeeDescription = (id) => (e) => {
    //     calculatedCosts.fees[id].description = e.target.value
    //     setCalculatedCosts({ ...calculatedCosts })
    // }

    const deleteFee = (id) => (e) => {
        setConfirmModal({
            open: true,
            text: `Are you sure you want to delete this ${calculatedCosts.fees[id].name} fee?`,
            callback: (confirmation) => {
                if (confirmation) {
                    delete calculatedCosts.fees[id]
                    setCalculatedCosts({ ...calculatedCosts })
                }
                setConfirmModal({ ...confirmModal, open: false, text: `Are you sure you want to delete this fee?` })
            }
        })
    }

    const addFee = (e) => {
        const new_id = crypto.randomUUID()
        calculatedCosts.fees[new_id] = {
            id: new_id,
            new: true,
            name: '',
            description: '',
            calculation_data: { fee_type: 'Flat Rate' },
            edited: false,
            sku: '',
            actual_cost: 0,
            estimated_cost: 0,
            service_order_fee_id: undefined,
            date_created: dayjs.utc()
        }
        setCalculatedCosts({ ...calculatedCosts })
    }

    const updateAdjustmentSku = (id) => (e) => {
        calculatedCosts.adjustments[id].sku = e.target.value
        setCalculatedCosts({ ...calculatedCosts })
    }

    const updateAdjustmentValue = (id) => (e) => {
        calculatedCosts.adjustments[id].actual_cost = parseMoneyInput(calculatedCosts.adjustments[id].actual_cost, e.target.value, { allow_empty: true, allow_negative: true })
        setCalculatedCosts({ ...calculatedCosts })
    }

    const updateAdjustmentDescription = (id) => (e) => {
        calculatedCosts.adjustments[id].description = e.target.value
        setCalculatedCosts({ ...calculatedCosts })
    }

    const deleteAdjustment = (id) => (e) => {
        setConfirmModal({
            open: true,
            text: `Are you sure you want to delete this adjustment?`,
            callback: (confirmation) => {
                if (confirmation) {
                    delete calculatedCosts.adjustments[id]
                    setCalculatedCosts({ ...calculatedCosts })
                }
                setConfirmModal({ ...confirmModal, open: false, text: `Are you sure you want to delete this adjustment?` })
            }
        })
    }

    const addAdjustment = (e) => {
        const new_id = crypto.randomUUID()
        calculatedCosts.adjustments[new_id] = {
            id: new_id,
            new: true,
            description: '',
            sku: serviceOrder.location_service_fees?.adjustment_sku ?? '',
            actual_cost: 0,
            date_created: dayjs.utc()
        }
        setCalculatedCosts({ ...calculatedCosts })
    }

    const quoted_shop_fees = serviceOrder.service_order_charges.find((x) => x.charge_type === 'SHOP_FEES')?.estimated_cost
    const total_shop_fees = calculatedCosts.shop_fees_manual ?? calculatedCosts.shop_fees_calculated // Do not parse float, keep as string
    const total_adjustments = Object.values(calculatedCosts.adjustments).reduce((acc, adjustment) => acc + parseFloat(adjustment.actual_cost), 0)
    const total_fees = Object.values(calculatedCosts.fees).reduce((acc, fee) => acc + parseFloat(isQuote ? fee.estimated_cost : fee.actual_cost), 0)
    let total_warranties_charges = 0
    Object.keys(warrantiedCharges).forEach((charge_id) => {
        const estimate = Math.round(Object.keys(warrantiedCharges[charge_id] ?? {}).reduce((accumulator, warranty_id) => accumulator + parseFloat(warrantiedCharges[charge_id][warranty_id].association.estimated_coverage), 0) * 100) / 100
        total_warranties_charges += estimate
    });
    const total_extras = parseFloat(total_shop_fees ?? 0) + parseFloat(total_adjustments) + parseFloat(total_fees) - parseFloat(total_warranties_charges)

    return (
        (<Box>
            <Grid container sx={{ mb: 3 }} columns={{ sm: 6, md: 12 }} rowSpacing={1}>
                <Grid size={12}>
                    <h2>
                        <b>Extras</b>
                    </h2>
                </Grid>
            </Grid>
            <Grid container rowSpacing={1} sx={{ border: `1px solid ${borderColor}`, borderRadius: '16px' }}>
                <Grid container sx={{ borderBottom: `1px solid ${borderColor}` }} size={12}>
                    <Grid sx={{ pl: 2 }} size={2}>
                        <span>Type</span>
                    </Grid>
                    <Grid size={3}>
                        <span>Value</span>
                    </Grid>
                    <Grid size={5}>
                        <span>Description (Customer Visible)</span>
                    </Grid>
                    <Grid sx={{ textAlign: 'right', pr: 4 }} size={2}>
                        <span>Total</span>
                    </Grid>
                </Grid>
                {serviceOrder.service_order_charges.find((x) => x.charge_type === 'SHOP_FEES')?.id !== undefined &&
                    <Grid container alignItems={'center'} size={12} rowSpacing={0}>
                        {/* SHOP FEES ROW */}
                        <Grid sx={{ pl: 2 }} size={2}>
                            <span>Shop Fees</span>
                        </Grid>
                        <Grid size={3}>
                            <TextField
                                margin="dense"
                                id="shop_fees"
                                label={`Shop Fees${total_shop_fees !== calculatedCosts.shop_fees_calculated ? ' (Manual)' : ''}`}
                                type="number"
                                inputMode="numeric"
                                size="small"
                                value={total_shop_fees.toString()}
                                onChange={(e) => {
                                    setCalculatedCosts({ ...calculatedCosts, shop_fees_manual: parseMoneyInput(total_shop_fees, e.target.value, { allow_negative: true, allow_empty: true }) })
                                }
                                }
                                helperText={`Auto: ${moneyFormat(calculatedCosts.shop_fees_calculated)} ${!isQuote ? `| Quoted: ${quoted_shop_fees !== undefined ? moneyFormat(quoted_shop_fees) : 'N/A'}` : ''}`}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {<AttachMoneyIcon />}
                                            </InputAdornment>
                                        )
                                    }
                                }}
                                color={total_shop_fees !== calculatedCosts.shop_fees_calculated ? 'warning' : 'primary'}
                                focused={total_shop_fees !== calculatedCosts.shop_fees_calculated}
                            />
                        </Grid>
                        <Grid size={5}>
                            <span></span>
                        </Grid>
                        <Grid sx={{ textAlign: 'right', pr: 4 }} size={2}>
                            <span>${moneyFormat(calculatedCosts.shop_fees_manual ?? calculatedCosts.shop_fees_calculated)}</span>
                        </Grid>
                        {Object.values(warrantiedCharges[serviceOrder.service_order_charges.find((x) => x.charge_type === 'SHOP_FEES')?.id] ?? {}).map((warranty) => {
                            const shop_fees_warranties = warrantiedCharges[serviceOrder.service_order_charges.find((x) => x.charge_type === 'SHOP_FEES')?.id] ?? {}
                            let shop_fees_total_warranty = 0
                            Object.keys(shop_fees_warranties).forEach((warranty_id) => {
                                const estimate = Math.round(parseFloat(shop_fees_warranties[warranty_id].association.estimated_coverage) * 100) / 100
                                shop_fees_total_warranty += estimate
                            })
                            return (<WarrantyLine
                                key={warranty.id}
                                amount={warranty.association.estimated_coverage}
                                total_covered={shop_fees_total_warranty}
                                total={parseFloat(total_shop_fees)}
                            />)
                        })}
                    </Grid>
                }
                {Object.values(calculatedCosts.fees ?? {}).toSorted((a, b) => dayjs.utc(a.date_created).unix() - dayjs.utc(b.date_created).unix()).map((fee) => (
                    <Grid key={fee.id} container alignItems={'center'} sx={{ borderTop: `1px solid ${borderColor}` }} size={12}>
                        {/* FEES ROW */}
                        <Grid sx={{ pl: 2, pr: 2 }} size={2} alignItems={'center'}>
                            {!fee.new && <span>{fee.name}</span>}
                            {fee.new &&
                                <FormControl size="small" variant="standard" fullWidth>
                                    <InputLabel id="new-service-order-fee">Service Order Fee</InputLabel>
                                    <Select
                                        margin="dense"
                                        labelId="new-service-order-fee"
                                        label="Fee Type"
                                        placeholder='Select Service Order Fee...'
                                        onChange={updateFeeType(fee.id)}
                                        value={fee.service_order_fee_id ?? ''}
                                    >
                                        {serviceOrderFees.map((fee) =>
                                            <MenuItem
                                                key={fee.id}
                                                value={fee.id}
                                            >
                                                {fee.name}
                                            </MenuItem>
                                        )}
                                    </Select>
                                </FormControl>
                            }
                        </Grid>
                        <Grid size={3}>
                            <TextField
                                margin="dense"
                                id="change"
                                label="Price"
                                type="number"
                                inputMode="numeric"
                                size="small"
                                value={(isQuote ? fee.estimated_cost : fee.actual_cost).toString()}
                                onChange={updateFeeValue(fee.id)}
                                helperText={`${!isQuote ? `Quoted: ${fee.estimated_cost ? moneyFormat(fee.estimated_cost) : 'N/A'}` : ''}`}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {<AttachMoneyIcon />}
                                            </InputAdornment>
                                        )
                                    }
                                }}
                                disabled={fee.calculation_data.editable !== true}
                            />
                        </Grid>
                        <Grid container size={5} alignItems={'center'} wrap="nowrap">
                            <Grid size="grow">
                                <span>{fee.description}</span>
                            </Grid>
                            <Grid size="auto">
                                <IconButton aria-label="delete" onClick={deleteFee(fee.id)} sx={{ verticalAlign: 'text-bottom' }}>
                                    <DeleteIcon color="error" />
                                </IconButton>
                            </Grid>
                        </Grid>
                        <Grid sx={{ textAlign: 'right', pr: 4 }} size={2}>
                            <span>${moneyFormat(isQuote ? fee.estimated_cost : fee.actual_cost)}</span>
                        </Grid>
                        {Object.values(warrantiedCharges[fee.id] ?? {}).map((warranty) => {
                            const warranties = warrantiedCharges[fee.id] ?? {}
                            let total_warranty = 0
                            Object.keys(warranties).forEach((warranty_id) => {
                                const estimate = Math.round(parseFloat(warranties[warranty_id].association.estimated_coverage) * 100) / 100
                                total_warranty += estimate
                            })
                            return (<WarrantyLine
                                key={warranty.id}
                                amount={warranty.association.estimated_coverage}
                                total_covered={total_warranty}
                                total={parseFloat(isQuote ? fee.estimated_cost : fee.actual_cost)}
                            />)
                        })}
                    </Grid>
                )
                )}
                {Object.values(calculatedCosts.adjustments ?? {}).toSorted((a, b) => dayjs.utc(a.date_created).unix() - dayjs.utc(b.date_created).unix()).map((adjustment) => (
                    <Grid key={adjustment.id} container alignItems={'center'} sx={{ borderTop: `1px solid ${borderColor}` }} size={12}>
                        {/* ADJUSTMENT ROW */}
                        <Grid sx={{ pl: 2 }} size={2}>
                            <span>Adjustment</span>
                        </Grid>
                        <Grid size={3}>
                            <TextField
                                margin="dense"
                                id="change"
                                label="Change"
                                type="number"
                                inputMode="numeric"
                                size="small"
                                value={adjustment.actual_cost.toString()}
                                onChange={updateAdjustmentValue(adjustment.id)}
                                helperText={`${!isQuote ? `Quoted: ${adjustment.estimated_cost ? moneyFormat(adjustment.estimated_cost) : 'N/A'}` : ''}`}
                                slotProps={{
                                    input: {
                                        startAdornment: (
                                            <InputAdornment position="start">
                                                {<AttachMoneyIcon />}
                                            </InputAdornment>
                                        )
                                    }
                                }}
                            />
                            <TextField
                                margin="dense"
                                id="adjustment_sku"
                                label="SKU"
                                type="text"
                                size="small"
                                value={adjustment.sku ?? ''}
                                onChange={updateAdjustmentSku(adjustment.id)}
                            />
                        </Grid>
                        <Grid container size={5} alignItems={'center'} wrap="nowrap">
                            <Grid size="grow">
                                <TextField
                                    margin="dense"
                                    id="description"
                                    type="text"
                                    placeholder="Description of Adjustment"
                                    fullWidth
                                    variant="filled"
                                    multiline
                                    minRows={2}
                                    value={adjustment.description}
                                    onChange={updateAdjustmentDescription(adjustment.id)}
                                    slotProps={{
                                        inputLabel: { shrink: true },
                                        input: { sx: { pt: 1 } },
                                    }}
                                    sx={{ mt: 0 }}
                                />
                            </Grid>
                            <Grid size="auto">
                                <IconButton aria-label="delete" onClick={deleteAdjustment(adjustment.id)}>
                                    <DeleteIcon color="error" />
                                </IconButton>
                            </Grid>
                        </Grid>
                        <Grid sx={{ textAlign: 'right', pr: 4 }} size={2}>
                            <span>${moneyFormat(adjustment.actual_cost)}</span>
                        </Grid>
                        {Object.values(warrantiedCharges[adjustment.id] ?? {}).map((warranty) => {
                            const warranties = warrantiedCharges[adjustment.id] ?? {}
                            let total_warranty = 0
                            Object.keys(warranties).forEach((warranty_id) => {
                                const estimate = Math.round(parseFloat(warranties[warranty_id].association.estimated_coverage) * 100) / 100
                                total_warranty += estimate
                            })
                            return (<WarrantyLine
                                key={warranty.id}
                                amount={warranty.association.estimated_coverage}
                                total_covered={total_warranty}
                                total={parseFloat(adjustment.actual_cost)}
                            />)
                        })}
                    </Grid>
                )
                )}
                <Grid container alignItems={'center'} sx={{ borderTop: `1px solid ${borderColor}` }} size={12}>
                    <Grid sx={{ textAlign: 'left', pl: 4, pt: 1 }} size='auto'>
                        <Button onClick={addAdjustment} variant="contained" color="primary"><AddCircleSharpIcon /><Box sx={{ pl: 1 }}>Add Adjustment</Box></Button>
                    </Grid>
                    <Grid sx={{ textAlign: 'left', pl: 4, pt: 1 }} size='auto'>
                        <Button onClick={addFee} variant="contained" color="primary"><AddCircleSharpIcon /><Box sx={{ pl: 1 }}>Add Fee</Box></Button>
                    </Grid>
                    <Grid size='grow'>
                    </Grid>
                </Grid>
                <Grid container alignItems={'center'} sx={{ borderTop: `1px solid ${borderColor}`, pb: 1 }} size={12}>
                    {/* TOTAL ROW */}
                    <Grid sx={{ pl: 2 }} size={10}>
                        <span>Total</span>
                    </Grid>
                    <Grid sx={{ textAlign: 'right', pr: 4 }} size={2}>
                        <span><b>${`${moneyFormat(total_extras)}`}</b></span>
                    </Grid>
                </Grid>
            </Grid>
            <ConfirmModal
                {...confirmModal}
            />
        </Box >)
    );
}